import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { Card, Dato, EstadoVacio } from '@/components/Card';
import {
  verInventario,
  resumenInventario,
  movimientosDe,
  verificarInventario,
  repararInventario,
  type RenglonInventario,
} from '@/db/repos/inventory';
import { formatearCentavos } from '@/domain/money';
import { formatearMedio } from '@/domain/dates';
import { TIPOS_MOVIMIENTO } from '@/db/types';
import { mensajeDeError } from '@/db/tx';

export function InventoryPage() {
  const inventario = useLiveQuery(() => verInventario(), []);
  const resumen = useLiveQuery(() => resumenInventario(), []);
  const [detalle, setDetalle] = useState<RenglonInventario | null>(null);

  if (detalle !== null) {
    return <DetalleInsumo renglon={detalle} onVolver={() => setDetalle(null)} />;
  }

  const conExistencia = inventario?.filter((r) => r.existenciaBase !== 0) ?? [];
  const sinExistencia = inventario?.filter((r) => r.existenciaBase === 0) ?? [];

  return (
    <div className="space-y-5">
      <PageHeader titulo="Inventario" descripcion="Qué tienes y cuánto vale" />

      {resumen !== undefined && resumen.insumos > 0 && (
        <Card>
          <dl className="divide-y divide-borde">
            <Dato
              etiqueta="Valor del inventario"
              valor={formatearCentavos(resumen.valorTotalCentavos)}
              detalle="Solo lo que tiene costo conocido"
            />
            <Dato
              etiqueta="Insumos con existencia"
              valor={`${resumen.conExistencia} de ${resumen.insumos}`}
            />
            {resumen.sinCostoConocido > 0 && (
              <Dato
                etiqueta="Con cantidad sin costo"
                valor={String(resumen.sinCostoConocido)}
                detalle="Registrados como saldo inicial sin precio"
              />
            )}
          </dl>
        </Card>
      )}

      {inventario === undefined && <p className="text-sm text-texto-suave">Cargando…</p>}

      {inventario !== undefined && inventario.length === 0 && (
        <EstadoVacio
          titulo="Todavía no hay insumos"
          descripcion="El inventario se llena al registrar compras y saldos iniciales."
        />
      )}

      {conExistencia.length > 0 && (
        <ul className="space-y-2">
          {conExistencia.map((renglon) => (
            <RenglonLista
              key={renglon.insumo.id}
              renglon={renglon}
              onAbrir={() => setDetalle(renglon)}
            />
          ))}
        </ul>
      )}

      {sinExistencia.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-texto-suave">Sin existencia</h2>
          <ul className="space-y-2">
            {sinExistencia.map((renglon) => (
              <RenglonLista
                key={renglon.insumo.id}
                renglon={renglon}
                onAbrir={() => setDetalle(renglon)}
              />
            ))}
          </ul>
        </section>
      )}

      {inventario !== undefined && inventario.length > 0 && <Verificacion />}
    </div>
  );
}

// ---------------------------------------------------------------------------

function RenglonLista({
  renglon,
  onAbrir,
}: {
  renglon: RenglonInventario;
  onAbrir: () => void;
}) {
  const { insumo } = renglon;
  const negativo = renglon.existenciaBase < 0;

  return (
    <li>
      <button
        type="button"
        onClick={onAbrir}
        className="w-full rounded-panel border border-borde bg-superficie p-4 text-left active:bg-superficie-suave"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold text-texto">{insumo.nombre}</p>
            {renglon.sinCosto !== null && (
              <p className="mt-0.5 text-xs text-aviso">
                {renglon.sinCosto} sin costo registrado
              </p>
            )}
            {renglon.origenCosto === 'ultima_compra' && (
              <p className="mt-0.5 text-xs text-texto-tenue">
                Costo de la última compra
              </p>
            )}
          </div>
          <div className="shrink-0 text-right">
            <p
              className={`font-semibold tabular-nums ${negativo ? 'text-alerta' : 'text-texto'}`}
            >
              {renglon.existencia}
            </p>
            {renglon.costoPorUnidadCompra !== null && (
              <p className="text-xs tabular-nums text-texto-suave">
                {formatearCentavos(renglon.costoPorUnidadCompra)} / {renglon.unidadCosto}
              </p>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------

function DetalleInsumo({
  renglon,
  onVolver,
}: {
  renglon: RenglonInventario;
  onVolver: () => void;
}) {
  const movimientos = useLiveQuery(() => movimientosDe(renglon.insumo.id), [renglon.insumo.id]);

  return (
    <div className="space-y-5">
      <PageHeader titulo={renglon.insumo.nombre} descripcion="Existencia y movimientos" />

      <Card>
        <dl className="divide-y divide-borde">
          <Dato etiqueta="Existencia" valor={renglon.existencia} />
          {renglon.sinCosto !== null && (
            <Dato
              etiqueta="Sin costo registrado"
              valor={renglon.sinCosto}
              detalle="No afecta el costo promedio"
            />
          )}
          {renglon.costoPorUnidadCompra !== null && (
            <Dato
              etiqueta={`Costo por ${renglon.unidadCosto}`}
              valor={formatearCentavos(renglon.costoPorUnidadCompra)}
              detalle={
                renglon.origenCosto === 'promedio'
                  ? 'Promedio ponderado de tus compras'
                  : 'Tomado de la última compra'
              }
            />
          )}
          <Dato
            etiqueta="Valor en inventario"
            valor={formatearCentavos(renglon.valorCentavos)}
          />
        </dl>
      </Card>

      <section className="space-y-2">
        <h2 className="text-base font-semibold text-texto">Movimientos</h2>

        {movimientos === undefined && <p className="text-sm text-texto-suave">Cargando…</p>}

        {movimientos !== undefined && movimientos.length === 0 && (
          <EstadoVacio titulo="Sin movimientos" />
        )}

        {movimientos !== undefined && movimientos.length > 0 && (
          <ul className="space-y-1.5">
            {movimientos.map((m) => (
              <li key={m.id}>
                <Card>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-texto">
                        {TIPOS_MOVIMIENTO[m.tipo] ?? m.tipo}
                      </p>
                      <p className="text-xs text-texto-suave">{formatearMedio(m.fecha)}</p>
                      {!m.afectaValuacion && (
                        <p className="text-xs text-aviso">Sin costo</p>
                      )}
                      {m.costoImputado && (
                        <p className="text-xs text-aviso">Costo estimado</p>
                      )}
                      {m.motivo !== null && (
                        <p className="mt-0.5 text-xs text-texto-tenue">{m.motivo}</p>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      <p
                        className={`text-sm font-semibold tabular-nums ${
                          m.cantidadBase >= 0 ? 'text-exito' : 'text-texto'
                        }`}
                      >
                        {m.cantidadBase >= 0 ? '+' : ''}
                        {m.cantidadLegible}
                      </p>
                      {m.valorCentavos !== 0 && (
                        <p className="text-xs tabular-nums text-texto-suave">
                          {formatearCentavos(m.valorCentavos)}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Button variante="secundario" onClick={onVolver}>
        Volver al inventario
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Verificacion del inventario contra el libro de movimientos (Req. 18.4).
 *
 * Los campos de existencia son una cache; la verdad esta en el libro. Este
 * boton compara ambos y, si hay descuadre, lo repara reescribiendo la cache sin
 * tocar ningun movimiento.
 */
function Verificacion() {
  const [estado, setEstado] = useState<
    | { fase: 'inactivo' }
    | { fase: 'revisando' }
    | { fase: 'cuadra' }
    | { fase: 'descuadre'; cuantos: number }
    | { fase: 'reparado'; cuantos: number }
    | { fase: 'error'; mensaje: string }
  >({ fase: 'inactivo' });

  async function revisar() {
    setEstado({ fase: 'revisando' });
    try {
      const problemas = await verificarInventario();
      setEstado(
        problemas.length === 0
          ? { fase: 'cuadra' }
          : { fase: 'descuadre', cuantos: problemas.length },
      );
    } catch (e) {
      setEstado({ fase: 'error', mensaje: mensajeDeError(e) });
    }
  }

  async function reparar() {
    setEstado({ fase: 'revisando' });
    try {
      const cuantos = await repararInventario();
      setEstado({ fase: 'reparado', cuantos });
    } catch (e) {
      setEstado({ fase: 'error', mensaje: mensajeDeError(e) });
    }
  }

  return (
    <section className="space-y-2 border-t border-borde pt-5">
      <h2 className="text-sm font-semibold text-texto-suave">Verificación</h2>
      <p className="text-xs text-texto-suave">
        Compara las existencias con el historial de movimientos, que es la fuente de verdad.
      </p>

      <Button
        variante="secundario"
        disabled={estado.fase === 'revisando'}
        onClick={() => void revisar()}
      >
        {estado.fase === 'revisando' ? 'Revisando…' : 'Revisar inventario'}
      </Button>

      {estado.fase === 'cuadra' && (
        <Banner tono="exito" titulo="Todo cuadra">
          Las existencias coinciden con el historial.
        </Banner>
      )}

      {estado.fase === 'descuadre' && (
        <>
          <Banner tono="aviso" titulo={`${estado.cuantos} insumo(s) con diferencias`}>
            Se puede corregir recalculando desde el historial. No se borra ningún movimiento.
          </Banner>
          <Button onClick={() => void reparar()}>Corregir desde el historial</Button>
        </>
      )}

      {estado.fase === 'reparado' && (
        <Banner tono="exito" titulo={`${estado.cuantos} insumo(s) corregido(s)`}>
          Las existencias se recalcularon desde el historial.
        </Banner>
      )}

      {estado.fase === 'error' && (
        <Banner tono="alerta" titulo="No se pudo revisar">
          {estado.mensaje}
        </Banner>
      )}
    </section>
  );
}
